import { m } from "framer-motion";
import { styled } from "@mui/material/styles";
import { Button, Typography, Container, Box } from "@mui/material";
import Page from "components/Page";
import { MotionContainer, varBounce } from "components/animate";
import Image from "components/image/Image";
import LogoOnlyView from "layouts/LogoOnlyLayout";

import Error428Page from "./Error428Page";
import Error404Page from "./Error404Page";
import Error403Page from "./Error403Page";
import Error500Page from "./Error500Page";

const ContentStyle = styled("div")(({ theme }) => ({
  maxWidth: 480,
  margin: "auto",
  minHeight: "100vh",
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  padding: theme.spacing(12, 0),
}));

type Props = {
  onClicked: () => void;
  error: any;
};

export default function ErrorBoundaryPage({ onClicked, error }: Props) {
  const errorStatus = error?.response?.status;

  if (errorStatus === 403) {
    return <Error403Page />;
  }

  if (errorStatus === 404) {
    return <Error404Page />;
  }

  if (errorStatus === 428) {
    return <Error428Page />;
  }

  if (errorStatus === 500) {
    return <Error500Page />;
  }

  return (
    <Page title="Boundary Error" appName={process.env.REACT_APP_CLIENT_NAME!}>
      <LogoOnlyView />
      <Container component={MotionContainer}>
        <ContentStyle sx={{ textAlign: "center", alignItems: "center" }}>
          <m.div variants={varBounce().in}>
            <Image
              visibleByDefault
              disabledEffect
              src="/assets/illustrations/500.svg"
              alt="login"
            />
          </m.div>

          <m.div variants={varBounce().in}>
            <Typography variant="h3" sx={{ px: 5, mt: 2, mb: 2 }}>
              Oops, we ran into{" "}
              <Typography variant="h3" component="span" color="primary">
                an error
              </Typography>
            </Typography>
            <Typography sx={{ color: "text.secondary", mt: 2, mb: 2 }}>
              Sorry, this is not working as expected. We now know about this and will be working to
              fix it.
            </Typography>

            <Typography
              sx={{ color: "text.secondary", mt: 3, textAlign: "left", fontWeight: "bold" }}
            >
              In the meantime, here is what you can do:
            </Typography>
            <Typography
              sx={{ color: "text.secondary", pl: 2, mb: 2, textAlign: "left" }}
              component="div"
            >
              <ul>
                <li>Refresh the page (sometimes this helps)</li>
              </ul>
            </Typography>
          </m.div>

          <Box sx={{ mt: 3 }}>
            <Button size="large" sx={{ ml: 2 }} variant="contained" onClick={onClicked}>
              Try Again
            </Button>
          </Box>
        </ContentStyle>
      </Container>
    </Page>
  );
}
